'use client';

import { useEffect, useRef } from 'react';

interface PaymentCheckoutProps {
  provider: string;
  widgetData: Record<string, unknown>;
  signature?: string;
  onSuccess: (data: unknown) => void;
  onError: (error: unknown) => void;
  onClose?: () => void;
}

export default function PaymentCheckout({
  provider,
  widgetData,
  signature,
  onSuccess,
  onError,
  onClose,
}: PaymentCheckoutProps) {
  const initiatedRef = useRef<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);

  // Keep callback refs up to date without triggering the effect
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onCloseRef.current = onClose;

  useEffect(() => {
    // Derive a unique key for this payment to prevent duplicate initiation
    const paymentKey = provider === 'wayforpay'
      ? String(widgetData.orderReference || '')
      : String(widgetData.data || '').substring(0, 50);

    if (!paymentKey || initiatedRef.current === paymentKey) return;
    initiatedRef.current = paymentKey;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;
    const MAX_RETRIES = 50; // 5 seconds max
    let retryCount = 0;

    if (provider === 'wayforpay') {
      initWayforpay();
    } else if (provider === 'liqpay') {
      initLiqpay();
    }

    function initWayforpay() {
      let scriptFailed = false;

      // Check if script already exists in DOM
      if (!document.getElementById('widget-wfp-script')) {
        const script = document.createElement('script');
        script.src = 'https://secure.wayforpay.com/server/pay-widget.js';
        script.id = 'widget-wfp-script';
        script.async = true;
        script.onerror = () => {
          scriptFailed = true;
          if (!cancelled) onErrorRef.current(new Error('Failed to load payment widget'));
        };
        document.body.appendChild(script);
      }

      const startPayment = () => {
        if (cancelled || scriptFailed) return;
        if (window.Wayforpay) {
          const wayforpay = new window.Wayforpay();
          wayforpay.run(
            {
              ...widgetData,
              authorizationType: 'SimpleSignature',
              merchantSignature: signature,
            },
            function (response: unknown) {
              if (!cancelled) onSuccessRef.current(response);
            },
            function (response: unknown) {
              if (cancelled) return;
              // WayForPay uses decline with reasonCode 1135 for user cancellation
              const resp = response as Record<string, unknown> | undefined;
              const isCancelled =
                resp?.transactionStatus === 'Declined' &&
                String(resp?.reasonCode) === '1135';

              if (isCancelled && onCloseRef.current) {
                onCloseRef.current();
              } else {
                onErrorRef.current(response);
              }
            }
          );
        } else if (retryCount < MAX_RETRIES) {
          retryCount++;
          retryTimer = setTimeout(startPayment, 100);
        }
      };

      startPayment();
    }

    function initLiqpay() {
      const startCheckout = () => {
        if (cancelled || !window.LiqPayCheckout) return;

        const checkout = window.LiqPayCheckout.init({
          data: String(widgetData.data),
          signature: String(widgetData.signature),
          embedTo: '#liqpay-checkout-container',
          mode: 'embed',
          language: 'uk',
        });

        checkout.on('liqpay.callback', function (data: unknown) {
          if (cancelled) return;
          const resp = data as Record<string, unknown>;
          const status = String(resp.status || '');

          if (status === 'success' || status === 'sandbox') {
            onSuccessRef.current(data);
          } else if (status === 'failure' || status === 'error') {
            onErrorRef.current(data);
          }
          // Intermediate statuses (wait_secure, processing, wait_accept, wait_card)
          // are ignored – the widget stays open and LiqPay fires another callback
          // with the final status after the user completes 3D Secure / Privat24 auth.
        });

        checkout.on('liqpay.ready', function () {
          // Widget is ready
        });

        checkout.on('liqpay.close', function () {
          if (!cancelled) onCloseRef.current?.();
        });
      };

      // Use LiqPay's official callback pattern for reliable script-load detection
      if (window.LiqPayCheckout) {
        startCheckout();
      } else {
        (window as unknown as Record<string, unknown>).LiqPayCheckoutCallback = () => {
          if (!cancelled) startCheckout();
        };

        if (!document.getElementById('widget-liqpay-script')) {
          const script = document.createElement('script');
          script.src = 'https://static.liqpay.ua/libjs/checkout.js';
          script.id = 'widget-liqpay-script';
          script.async = true;
          document.body.appendChild(script);
        }
      }
    }

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, signature,
    // Use stable keys instead of object reference to avoid infinite re-runs
    provider === 'wayforpay' ? widgetData.orderReference : widgetData.data,
  ]);

  // WayForPay manages its own overlay – render nothing
  if (provider !== 'liqpay') {
    return null;
  }

  // LiqPay: render our own modal backdrop + wide container for embed mode.
  // No overflow-y-auto on the container – LiqPay's iframe manages its own scroll.
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[720px]">
        {/* Close button */}
        <button
          onClick={() => onClose?.()}
          className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        {/* LiqPay widget renders inside this div via embedTo */}
        <div id="liqpay-checkout-container" className="rounded-2xl overflow-hidden" />
      </div>
    </div>
  );
}
